import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, X } from "lucide-react";
import { useApp, formatINR, optimizedImage, imageSrcSet, handleImgError, minVariantPrice } from "../store";
import useDocumentMeta from "../hooks/useDocumentMeta";

export default function Catalog() {
  const { API } = useApp();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  // SEO-optimized titles + descriptions per category (keyword targeting)
  const FLORALS_KEY = "Floral & Absolute";
  const CATEGORY_META = {
    [FLORALS_KEY]: {
      title: "Buy Pure Floral Absolutes Online — Rose, Jasmine, Tuberose, Pink Lotus",
      description: "Buy pure floral absolutes online from House of Absolutes KA: Ruh Gulab (Rose Absolute), Jasmine Sambac Absolute, Jasmine Grandiflorum Absolute, Natural Tuberose Absolute, Pink Lotus Absolute, Vanilla Absolute, Champaca Absolute. 100% natural, GC/MS verified, COA shipped. Worldwide shipping from Kannauj, India.",
      keywords: [
        "rose absolute", "jasmine absolute", "tuberose absolute", "pink lotus absolute",
        "lotus absolute", "buy rose absolute", "buy jasmine absolute",
        "jasmine sambac absolute", "jasmine grandiflorum absolute", "rose absolute oil",
        "natural tuberose absolute", "pure floral absolute online",
      ],
    },
    "Attar": {
      title: "Buy Traditional Indian Attars Online — Ruh Khus, Mitti, Gulab",
      description: "Buy traditional Indian attars online — Ruh Khus (wild vetiver), Mitti Attar (baked earth / petrichor), Gulab Attar (rose), distilled in Kannauj using deg-bhapka copper-still method. GC/MS verified, sandalwood-based.",
      keywords: ["Indian attar", "Ruh Khus", "Mitti attar", "Kannauj attar", "buy attar online"],
    },
    "Wood & Resin": {
      title: "Indian Sandalwood, Oud & Patchouli Oil Online",
      description: "Buy pure Indian Sandalwood (Mysore), Hindi Oud (Aquilaria malaccensis), Patchouli Oil, Vetiver, Cedarwood, and Nagarmotha (Cypriol) online. GC/MS verified, CITES-compliant where applicable.",
      keywords: ["Indian sandalwood", "Mysore sandalwood", "Oud", "Patchouli Oil", "Vetiver oil", "Nagarmotha Oil"],
    },
    "Resinoid": {
      title: "Buy Benzoin & Myrrh Resinoid Online — Pure Tree Resin Absolutes",
      description: "Buy pure natural resinoids online — Benzoin Resinoid (Styrax benzoin, Sumatra) and Myrrh Resinoid (Commiphora myrrha, Somalia). Solvent-extracted from tree gum-resins. Warm, balsamic, deeply fixative. GC/MS verified, full COA.",
      keywords: ["Benzoin Resinoid", "Myrrh Resinoid", "Styrax benzoin", "Commiphora myrrha", "natural resinoid", "amber fixative"],
    },
    "Herb & Grass": {
      title: "Lemongrass, Palmarosa, Citronella & Kashmir Lavender Oil",
      description: "Buy pure Indian aromatic herbs and grass oils — lemongrass, palmarosa, citronella, Kashmir lavender. Steam-distilled, GC/MS verified.",
      keywords: ["lemongrass oil", "palmarosa oil", "citronella oil", "Kashmir lavender oil"],
    },
  };
  const catMeta = CATEGORY_META[category];

  useDocumentMeta({
    title: catMeta?.title || (category ? `${category} Catalog` : "Catalog — All Distillations"),
    description: catMeta?.description ||
      (category
        ? `Buy pure ${category} oils & absolutes from Kannauj. Solvent extracted, steam distilled, copper-still attars. Full COA + GC/MS lab reports.`
        : "Browse our twenty rare distillations — Rose, Jasmine, Oud, Sandalwood, Mitti, Pink Lotus, Lavender, and more. Pure Indian florals from Kannauj."),
    keywords: catMeta?.keywords || [],
  });
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ categories: [], origins: [], extraction_methods: [] });
  const [q, setQ] = useState(params.get("q") || "");

  const origin = params.get("origin") || "";
  const extraction = params.get("extraction") || "";

  useEffect(() => {
    axios.get(`${API}/products/filters`).then(r => setFilters(r.data));
  }, [API]);

  useEffect(() => {
    const p = {};
    if (category) p.category = category;
    if (origin) p.origin = origin;
    if (extraction) p.extraction_method = extraction;
    if (q) p.q = q;
    axios.get(`${API}/products`, { params: p }).then(r => setProducts(r.data));
  }, [API, category, origin, extraction, q]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const activeFilters = useMemo(() => {
    const arr = [];
    if (category) arr.push({ k: "category", v: category });
    if (origin) arr.push({ k: "origin", v: origin });
    if (extraction) arr.push({ k: "extraction", v: extraction });
    return arr;
  }, [category, origin, extraction]);

  const CATEGORY_INTROS = {
    "Floral & Absolute": "Buy pure floral absolutes online — 100% natural, GC/MS verified, shipped with full Certificate of Analysis. Our collection includes Ruh Gulab / Rose Absolute (Rosa damascena), Jasmine Sambac Absolute (Mogra), Jasmine Grandiflorum Absolute, Natural Tuberose Absolute, Pink Lotus Absolute, Marigold (Genda), Oakmoss (Evernia prunastri), Vanilla Absolute, Champaca, and Betel Leaf Absolute — solvent-extracted from hand-harvested florals across India and used by haute parfumerie houses worldwide.",
    "Attar": "Traditional Indian attars, distilled in Kannauj using the centuries-old deg-bhapka (copper still + sandalwood receiver) method. Ruh Khus, Mitti, Gulab, Hina, Shamama Attar 120 — composed and aged perfumes that need no top notes added. The single most authentic perfumery tradition still practiced today.",
    "Wood & Resin": "Steam-distilled and hydro-distilled woody base notes — Indian sandalwood, agarwood (oud), cedarwood, patchouli, vetiver, nagarmotha. The structural foundation of every serious perfume composition.",
    "Resinoid": "Solvent-extracted resinoids from aromatic tree gums, balsams, and lichens — Benzoin Resinoid (Styrax benzoin, Sumatra), Myrrh Resinoid (Commiphora myrrha, Somalia). Warm, balsamic, deeply fixative — the base of every classical oriental, amber, and ecclesiastical composition. Resinoids retain more of the natural fixative acids than absolutes, giving unmatched longevity in fine fragrance and incense.",
    "Herb & Grass": "Citrus-herbal top notes, aromatic mids, and carrier oils — lemongrass, palmarosa, citronella, Kashmir lavender, cold-pressed Jojoba (Simmondsia chinensis) carrier. Bright, accessible, indispensable in soap, aromatherapy, and modern fresh perfumery.",
    "Spice": "Aromatic spice oils for warm, oriental, and gourmand compositions.",
  };
  const categoryIntro = CATEGORY_INTROS[category];

  return (
    <div className="pt-32 pb-24 px-6 md:px-12 max-w-[1500px] mx-auto" data-testid="catalog-page">
      <div className="mb-16">
        <div className="overline text-copper mb-4">The Library</div>
        <h1 className="headline-xl text-[clamp(3rem,7vw,7rem)]">{category || "The Catalog"}<span className="italic text-[#8A8D8A]">.</span></h1>
        {categoryIntro ? (
          <p className="text-base text-[#5C5E5C] mt-6 max-w-3xl leading-relaxed" data-testid="catalog-category-intro">{categoryIntro}</p>
        ) : (
          <p className="text-[#5C5E5C] mt-6 max-w-xl">Twenty distillations. One promise — pure, undiluted, traceable.</p>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-12">
        {/* Sidebar filters */}
        <aside className="lg:col-span-3">
          <div className="sticky top-32 space-y-10">
            <div>
              <div className="relative mb-8">
                <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8A8D8A]" strokeWidth={1.5} />
                <input
                  className="input-underline pl-7"
                  placeholder="Search rose, oud, aroma…"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  data-testid="catalog-search"
                />
              </div>
            </div>

            <FilterGroup title="Category" options={filters.categories} value={category} onChange={v => setFilter("category", v)} testidPrefix="filter-cat" />
            <FilterGroup title="Origin" options={filters.origins} value={origin} onChange={v => setFilter("origin", v)} testidPrefix="filter-origin" />
            <FilterGroup title="Extraction" options={filters.extraction_methods} value={extraction} onChange={v => setFilter("extraction", v)} testidPrefix="filter-ext" />

            {activeFilters.length > 0 && (
              <button onClick={() => setParams({})} className="overline text-xs text-copper hover:underline" data-testid="clear-filters">
                Clear all filters
              </button>
            )}
          </div>
        </aside>

        {/* Grid */}
        <div className="lg:col-span-9">
          <div className="flex justify-between items-center mb-8 pb-4 border-b border-[#E5E1D8]">
            <span className="overline text-xs text-[#5C5E5C]">{products.length} {products.length === 1 ? "essence" : "essences"}</span>
            {activeFilters.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {activeFilters.map(f => (
                  <button key={f.k} onClick={() => setFilter(f.k, "")} className="text-[10px] uppercase tracking-widest border border-[#E5E1D8] px-3 py-1 hover:border-copper flex items-center gap-1">
                    {f.v} <X size={12} />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-14">
            {products.map((p, i) => (
              <Link key={p.id} to={`/product/${p.id}`} className="product-tile group" data-testid={`product-card-${p.id}`}>
                <div className="aspect-[4/5] overflow-hidden bg-clay mb-5">
                  <img
                    src={optimizedImage(p.image_url, 700, 65)}
                    srcSet={imageSrcSet(p.image_url, [400, 600, 900], 65)}
                    sizes="(max-width: 640px) 100vw, (max-width: 1280px) 50vw, 33vw"
                    alt={`Buy ${p.name}${p.botanical_name ? ` (${p.botanical_name})` : ''} online — 100% pure natural absolute / oil from House of Absolutes KA, Kannauj`}
                    loading={i < 6 ? "eager" : "lazy"}
                    fetchpriority={i < 3 ? "high" : "auto"}
                    decoding="async"
                    width="700"
                    height="875"
                    onError={handleImgError(p)}
                    className="product-img w-full h-full object-cover"
                  />
                </div>
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="overline text-[10px] text-[#8A8D8A] mb-1">{p.category}</div>
                    <h3 className="font-serif-display text-2xl leading-tight">{p.name}</h3>
                    <p className="text-xs text-[#8A8D8A] italic mt-1">{p.botanical_name}</p>
                  </div>
                  <div className="text-right">
                    <div className="overline text-[10px] text-[#8A8D8A]">From</div>
                    <div className="font-serif-display text-xl">{formatINR(minVariantPrice(p))}</div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {products.length === 0 && (
            <div className="text-center py-24 text-[#8A8D8A] font-serif-display text-2xl italic">No essences match your search.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterGroup({ title, options, value, onChange, testidPrefix }) {
  return (
    <div>
      <div className="overline text-[10px] mb-4 text-copper">{title}</div>
      <ul className="space-y-2">
        {options.map(o => (
          <li key={o}>
            <button
              onClick={() => onChange(value === o ? "" : o)}
              className={`text-sm text-left w-full hover:text-copper transition-colors ${value === o ? "text-copper font-medium" : "text-[#1E1F1E]"}`}
              data-testid={`${testidPrefix}-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
            >
              {o}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
